import { useState } from "react";
import { reportsApi, getOrganizationId } from "./reportsApi";

const categoryGenerationOrder = ["Devices", "Vehicles", "Routes", "Stops", "Drivers", "Users"];

const ENTRY_EXIT_COLUMN_ORDER = {
  Vehicles: ["vehicleNumber", "vehicleName", "routeName", "stopName", "entryTime", "exitTime", "duration"],
  Routes: ["routeName", "vehicleNumber", "vehicleName", "stopName", "entryTime", "exitTime", "duration"],
  Stops: ["stopName", "vehicleNumber", "vehicleName", "routeName", "entryTime", "exitTime", "duration"],
};

const reorderEntryExitColumns = (columns, categoryKey) => {
  const order = ENTRY_EXIT_COLUMN_ORDER[categoryKey];
  if (!order || !Array.isArray(columns)) return columns;
  const byKey = new Map(columns.map((column) => [column.key, column]));
  const ordered = order.map((key) => byKey.get(key)).filter(Boolean);
  return ordered.length ? ordered : columns;
};

// These report previews contain cross-category charts. Fetching these support
// sections keeps every chart populated without adding extra visible tables.
const analyticsCategoryDependencies = {
  activity: ["Vehicles", "Routes", "Stops"],
  runtime: ["Vehicles", "Routes"],
};

const sortSelectedCategoryEntries = (selectedCategories) =>
  Object.entries(selectedCategories).sort(([a], [b]) => {
    const aIndex = categoryGenerationOrder.indexOf(a);
    const bIndex = categoryGenerationOrder.indexOf(b);
    return (aIndex === -1 ? 99 : aIndex) - (bIndex === -1 ? 99 : bIndex);
  });

const resolveRouteNames = (rows, routes) => {
  if (!Array.isArray(rows)) return [];

  const routeNameById = new Map(
    (routes || []).flatMap((route) => {
      const name = route.name || route.route_name;
      const ids = [route.id, route.routeId]
        .filter((id) => id !== null && id !== undefined && id !== "")
        .map(String);
      return name ? ids.map((id) => [id, name]) : [];
    })
  );

  return rows.map((row) => {
    const routeId = row?.routeId ?? row?.route_id;
    const currentRouteName = row?.routeName;
    const resolvedName =
      routeNameById.get(String(routeId ?? "")) ||
      routeNameById.get(String(currentRouteName ?? ""));

    return resolvedName && resolvedName !== currentRouteName
      ? { ...row, routeName: resolvedName }
      : row;
  });
};

export const useReportGenerator = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);

  const clearError = () => setError(null);
  const clearReportData = () => setReportData(null);

  const getCategoryRequest = ({
    categoryKey,
    selectedCategory,
    vehicles,
    routes,
    drivers,
    stops,
    users,
    devices = [],
  }) => {
    const { isAllSelected, selectedItems } = selectedCategory;

    if (categoryKey === "Devices") {
      return {
        category: "devices",
        devices,
        selectedIds: isAllSelected
          ? devices.map((device) => device.deviceId || device.id).filter(Boolean)
          : selectedItems
              .map((name) => {
                const matchedDevice = devices.find(
                  (device) => (device.deviceId || String(device.id || "")) === name
                );
                return matchedDevice?.deviceId || matchedDevice?.id;
              })
              .filter(Boolean),
      };
    }

    if (categoryKey === "Vehicles") {
      return {
        category: "vehicles",
        selectedIds: isAllSelected
          ? vehicles.map((v) => v.device_id)
          : selectedItems
              .map(
                (name) =>
                  vehicles.find(
                    (v) => (v.VehicleNumber || v.device_id) === name
                  )?.device_id
              )
              .filter((id) => id),
      };
    }

    if (categoryKey === "Routes") {
      return {
        category: "routes",
        selectedIds: isAllSelected
          ? routes.map((r) => r.id)
          : selectedItems
              .map(
                (name) =>
                  routes.find((r) => (r.name || r.route_name) === name)?.id
              )
              .filter((id) => id),
      };
    }

    if (categoryKey === "Drivers") {
      return {
        category: "drivers",
        selectedIds: isAllSelected
          ? drivers.map((d) => d.id)
          : selectedItems
              .map(
                (name) =>
                  drivers.find(
                    (d) => `${d.first_name} ${d.last_name}`.trim() === name
                  )?.id
              )
              .filter((id) => id),
      };
    }

    if (categoryKey === "Stops") {
      return {
        category: "stops",
        selectedIds: isAllSelected
          ? stops.map((s) => s.id)
          : selectedItems
              .map((name) => stops.find((s) => s.name === name)?.id)
              .filter((id) => id),
      };
    }

    if (categoryKey.toLowerCase() === "users") {
      return {
        category: "users",
        selectedIds: isAllSelected
          ? users.map((u) => u.id)
          : selectedItems
              .map(
                (name) =>
                  users.find(
                    (u) => (u.name || u.userName || u.email || "") === name
                  )?.id
              )
              .filter((id) => id),
      };
    }

    throw new Error(`Invalid category: ${categoryKey}`);
  };

  const getCategoryReportTitle = (categoryKey) => {
    const titleMap = {
      Vehicles: "Vehicle Report",
      Routes: "Route Report",
      Drivers: "Driver Report",
      Stops: "Stop Report",
      Users: "User Report",
      Devices: "Device Report",
      Sites: "Site Report",
    };

    return titleMap[categoryKey] || `${categoryKey} Report`;
  };

  const fetchReport = (reportType, params) => {
    if (reportType === "overspeed") {
      return reportsApi.getOverspeedReport(params);
    } else if (reportType === "activity") {
      return reportsApi.getActivityReport(params);
    } else if (reportType === "sos") {
      return reportsApi.getSosReport(params);
    } else if (reportType === "inventory") {
      return reportsApi.getInventoryReport(params);
    } else if (reportType === "runtime") {
      return reportsApi.getRuntimeReport(params);
    } else if (reportType === "trip") {
      return reportsApi.getTripReport(params);
    } else if (reportType === "deviation") {
      return reportsApi.getRouteDeviationReport(params);
    } else if (reportType === "halt") {
      return reportsApi.getHaltReport(params);
    } else if (reportType === "entryExit") {
      return reportsApi.getEntryExitReport(params);
    }

    throw new Error("Invalid report type");
  };

  const generateReport = async ({
    reportType,
    selectedCategories,
    dateOptions,
    vehicles,
    routes,
    drivers,
    stops,
    users, // Added users for inventory reports
    devices = [],
    siteId,
  }) => {
    setLoading(true);
    setError(null);

    try {
      const orgId = getOrganizationId();
      if (!orgId) {
        throw new Error("Organization ID is required. Please log in again.");
      }
      if (!dateOptions.duration) {
        throw new Error("Date option is required.");
      }
      if (
        dateOptions.duration === "custom" &&
        (!dateOptions.customDateRange.startDate ||
          !dateOptions.customDateRange.endDate)
      ) {
        throw new Error("Custom date range requires start and end dates.");
      }

      // Check if any category is selected
      if (!selectedCategories || Object.keys(selectedCategories).length === 0) {
        throw new Error(
          "Please select at least one category (Devices, Vehicles, Routes, Drivers, Stops, or Users)."
        );
      }

      const categoryEntries = sortSelectedCategoryEntries(selectedCategories);
      const sections = [];
      const activityAnalyticsSections = [];
      // Vehicles, Routes, and Stops with "select all" each replay the same
      // entry/exit scan. One response fills every selected table.
      const entryExitShared =
        reportType === "entryExit" &&
        categoryEntries.length > 1 &&
        categoryEntries.every(([, selectedCategory]) => selectedCategory.isAllSelected);

      const buildSectionParams = (categoryKey, selectedCategory) => {
        const { category, selectedIds } = getCategoryRequest({
          categoryKey,
          selectedCategory,
          vehicles,
          routes,
          drivers,
          stops,
          users,
          devices,
        });

        return {
          orgId,
          siteId: siteId || null,
          category,
          selectedIds,
          devices,
          selectAll: selectedCategory.isAllSelected,
          dateOption: dateOptions.duration,
          customStartDate: dateOptions.customDateRange.startDate,
          customEndDate: dateOptions.customDateRange.endDate,
        };
      };

      if (reportType === "activity") {
        const selectedCategoryKeys = new Set(categoryEntries.map(([key]) => key));
        const supportEntries = (analyticsCategoryDependencies.activity || [])
          .filter((categoryKey) => !selectedCategoryKeys.has(categoryKey))
          .map((categoryKey) => [categoryKey, { isAllSelected: true, selectedItems: [] }]);
        const requestEntries = [
          ...categoryEntries.map(([categoryKey, selectedCategory]) => ({
            categoryKey,
            selectedCategory,
            analyticsOnly: false,
          })),
          ...supportEntries.map(([categoryKey, selectedCategory]) => ({
            categoryKey,
            selectedCategory,
            analyticsOnly: true,
          })),
        ];
        const combined = await reportsApi.getCombinedActivityReport({
          orgId,
          siteId: siteId || null,
          dateOption: dateOptions.duration,
          customStartDate: dateOptions.customDateRange.startDate,
          customEndDate: dateOptions.customDateRange.endDate,
          sections: requestEntries.map(({ categoryKey, selectedCategory }) => {
            const { category, selectedIds } = getCategoryRequest({
              categoryKey,
              selectedCategory,
              vehicles,
              routes,
              drivers,
              stops,
              users,
              devices,
            });
            return {
              category,
              selectedIds,
              selectAll: selectedCategory.isAllSelected,
            };
          }),
        });
        const sectionByType = new Map(
          (combined.sections || []).map((section) => [section.reportType, section])
        );

        requestEntries.forEach(({ categoryKey, analyticsOnly }) => {
          const { category } = getCategoryRequest({
            categoryKey,
            selectedCategory: { isAllSelected: true, selectedItems: [] },
            vehicles,
            routes,
            drivers,
            stops,
            users,
            devices,
          });
          const sectionData = sectionByType.get(category) || { data: [], columns: [] };
          const section = {
            ...sectionData,
            data: resolveRouteNames(sectionData?.data, routes),
            sectionTitle: getCategoryReportTitle(categoryKey),
            categoryKey,
            ...(analyticsOnly ? { analyticsOnly: true } : {}),
          };
          if (analyticsOnly) {
            activityAnalyticsSections.push(section);
          } else {
            sections.push(section);
          }
        });
      } else if (entryExitShared) {
        const [categoryKey, selectedCategory] = categoryEntries[0];
        const params = buildSectionParams(categoryKey, selectedCategory);
        console.log("Report Parameters:", params);
        const sectionData = await fetchReport(reportType, params);
        const rows = resolveRouteNames(sectionData?.data, routes);

        categoryEntries.forEach(([sectionKey], index) => {
          sections.push({
            ...sectionData,
            summary: index === 0 ? sectionData.summary : undefined,
            columns: reorderEntryExitColumns(sectionData.columns, sectionKey),
            data: rows,
            sectionTitle: getCategoryReportTitle(sectionKey),
            categoryKey: sectionKey,
          });
        });
      } else {
        for (const [categoryKey, selectedCategory] of categoryEntries) {
          const params = buildSectionParams(categoryKey, selectedCategory);

          console.log("Report Parameters:", params);

          const sectionData = await fetchReport(reportType, params);
          sections.push({
            ...sectionData,
            data: resolveRouteNames(sectionData?.data, routes),
            sectionTitle: getCategoryReportTitle(categoryKey),
            categoryKey,
          });
        }
      }

      const selectedCategoryKeys = new Set(categoryEntries.map(([key]) => key));
      const missingAnalyticsCategories = (
        analyticsCategoryDependencies[reportType] || []
      ).filter((categoryKey) => !selectedCategoryKeys.has(categoryKey));

      const analyticsSupportSections = reportType === "activity"
        ? activityAnalyticsSections
        : await Promise.all(
        missingAnalyticsCategories.map(async (categoryKey) => {
          const { category, selectedIds } = getCategoryRequest({
            categoryKey,
            selectedCategory: { isAllSelected: true, selectedItems: [] },
            vehicles,
            routes,
            drivers,
            stops,
            users,
            devices,
          });
          const sectionData = await fetchReport(reportType, {
            orgId,
            siteId: siteId || null,
            category,
            selectedIds,
            devices,
            selectAll: true,
            dateOption: dateOptions.duration,
            customStartDate: dateOptions.customDateRange.startDate,
            customEndDate: dateOptions.customDateRange.endDate,
          });

          return {
            ...sectionData,
            data: resolveRouteNames(sectionData?.data, routes),
            sectionTitle: getCategoryReportTitle(categoryKey),
            categoryKey,
            analyticsOnly: true,
          };
        })
      );
      const analyticsSections = [...sections, ...analyticsSupportSections];

      const sharedRows = sections[0]?.data || [];
      const data = entryExitShared
        ? {
            ...sections[0],
            reportType,
            sections,
            analyticsSections,
            data: sharedRows,
            count: sharedRows.length,
            entryExitShared: true,
          }
        : sections.length === 1
          ? { ...sections[0], analyticsSections }
          : {
              reportType,
              sections,
              analyticsSections,
              data: sections.flatMap((section) => section.data || []),
              count: sections.reduce(
                (total, section) => total + (section.data?.length || 0),
                0
              ),
              columns: sections[0]?.columns || [],
            };

      // Deduplicate data by deviceId if needed
      // if (data?.data && Array.isArray(data.data)) {
      //   const uniqueData = [...new Map(data.data.map(item => [item.deviceId, item])).values()];
      //   data = { ...data, data: uniqueData, count: uniqueData.length };
      // }

      console.log("Report Data:", data); // Debug log
      setReportData(data);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to generate report";
      console.error("Report Error:", err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return {
    generateReport,
    loading,
    error,
    reportData,
    clearError,
    clearReportData,
  };
};
